import React from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createSupplier,
  updateSupplier,
} from "../redux/actions/supplierActions";
import { supplierActions } from "../redux/slices/supplier";
import { cleanFormData } from "../helpers/formHelpers";

import { SupplierResponseDto } from "../shared/dtos/supplier/SupplierResponseDto";
import { SupplierRequestDto } from "../shared/dtos/supplier/SupplierRequestDto";

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Street Line 1 is required"),
  streetLine2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State / Province is required"),
  postalCode: Yup.string().required("Postal Code is required"),
  country: Yup.string().required("Country is required"),
});

const supplierSchema = Yup.object().shape({
  name: Yup.string().required("Company name is required"),
  contactPerson: Yup.string().nullable(),
  phoneNumber: Yup.string().required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  productCategory: Yup.string().nullable(),
  taxId: Yup.string().nullable(),
  website: Yup.string()
    .url("Invalid website URL (must start with http:// or https://)")
    .nullable(),
  address: addressSchema,
});

interface CreateOrUpdateSupplierFormProps {
  supplier?: SupplierResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateSupplierForm: React.FC<CreateOrUpdateSupplierFormProps> = ({
  supplier = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: SupplierRequestDto = {
    name: supplier?.name || "",
    contactPerson: supplier?.contactPerson || "",
    phoneNumber: supplier?.phoneNumber || "",
    email: supplier?.email || "",
    productCategory: supplier?.productCategory || "",
    taxId: supplier?.taxId || "",
    website: supplier?.website || "",
    address: {
      streetLine1: supplier?.address?.streetLine1 || "",
      streetLine2: supplier?.address?.streetLine2 || "",
      city: supplier?.address?.city || "",
      stateOrProvince: supplier?.address?.stateOrProvince || "",
      postalCode: supplier?.address?.postalCode || "",
      country: supplier?.address?.country || "Kenya",
    },
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(supplierActions.setErrorUpdate(null))
            : () => dispatch(supplierActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={supplierSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: SupplierRequestDto = {
            name: rawCleaned.name,
            contactPerson: rawCleaned.contactPerson || null,
            phoneNumber: rawCleaned.phoneNumber,
            email: rawCleaned.email,
            productCategory: rawCleaned.productCategory || null,
            taxId: rawCleaned.taxId || null,
            website: rawCleaned.website || null,
            address: {
              streetLine1: rawCleaned.address.streetLine1,
              streetLine2: rawCleaned.address.streetLine2 || null,
              city: rawCleaned.address.city,
              stateOrProvince: rawCleaned.address.stateOrProvince,
              postalCode: rawCleaned.address.postalCode,
              country: rawCleaned.address.country,
            },
          };

          if (isEditing && supplier) {
            dispatch(
              updateSupplier({ id: supplier.id, values: cleanedValues }),
            );
          } else {
            dispatch(createSupplier(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<SupplierRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Company Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField name="name" label="Company Name" />
                <FormTextField
                  name="productCategory"
                  label="Product Category"
                  placeholder="e.g. Office Supplies"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField name="taxId" label="Tax ID / VAT No." />
                <FormTextField
                  name="website"
                  label="Website URL"
                  placeholder="https://..."
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Contact Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="contactPerson"
                  label="Contact Person Name"
                />
                <FormTextField name="phoneNumber" label="Phone Number" />
              </Box>

              <FormTextField name="email" label="Email Address" type="email" />

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Business Address
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Street Line 2 (Optional)"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField
                  name="address.stateOrProvince"
                  label="State / Province"
                />
                <FormTextField name="address.postalCode" label="Postal Code" />
                <FormTextField name="address.country" label="Country" />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Supplier"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddBusinessIcon />
                }
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateSupplierForm;
